import type { $Fetch } from 'ofetch';
import type { DisciplineGateway } from '@/application/port/out/DisciplineGateway';
import type {
  CreateDisciplineCommand,
  ListDisciplinesQuery,
  UpdateDisciplineCommand,
} from '@/application/port/in/dto/DisciplineCommand';
import type { Discipline } from '@/domain/model/Discipline';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse, SpringPage } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const PATH = '/api/v1/disciplines';

export class HttpDisciplineGateway implements DisciplineGateway {
  constructor(private readonly client: $Fetch) {}

  async list(query: ListDisciplinesQuery): Promise<Page<Discipline>> {
    const params: Record<string, string | number> = {};
    if (query.name) params['name'] = query.name;
    if (typeof query.page === 'number') params['page'] = query.page;
    if (typeof query.size === 'number') params['size'] = query.size;
    if (query.sort) params['sort'] = query.sort;

    const res = await this.client<ApiResponse<SpringPage<Discipline>>>(PATH, {
      method: 'GET',
      params,
    });
    assertApiSuccess(res);
    return {
      content: res.data.content,
      number: res.data.number,
      size: res.data.size,
      totalElements: res.data.totalElements,
      totalPages: res.data.totalPages,
      first: res.data.first,
      last: res.data.last,
    };
  }

  async getById(id: string): Promise<Discipline> {
    const res = await this.client<ApiResponse<Discipline>>(
      `${PATH}/${encodeURIComponent(id)}`,
      { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async create(command: CreateDisciplineCommand): Promise<Discipline> {
    const res = await this.client<ApiResponse<Discipline>>(PATH, {
      method: 'POST',
      body: command,
    });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, command: UpdateDisciplineCommand): Promise<Discipline> {
    const res = await this.client<ApiResponse<Discipline>>(
      `${PATH}/${encodeURIComponent(id)}`,
      { method: 'PUT', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async activate(id: string): Promise<Discipline> {
    const res = await this.client<ApiResponse<Discipline>>(
      `${PATH}/${encodeURIComponent(id)}/activate`,
      { method: 'PATCH' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async archive(id: string): Promise<void> {
    // BE 204 No Content — sem envelope.
    await this.client<void>(
      `${PATH}/${encodeURIComponent(id)}/archive`,
      { method: 'PATCH' },
    );
  }

  /** FASE C.5 — GET /api/v1/matriculas/{id}/disciplines (cache HTTP 5min server-side). */
  async listByMatricula(matriculaId: string): Promise<Discipline[]> {
    const res = await this.client<ApiResponse<Discipline[]>>(
      `/api/v1/matriculas/${encodeURIComponent(matriculaId)}/disciplines`,
      { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }
}
